import {
  PerspectiveCamera,
  OrthographicCamera,
  Vector3,
  MathUtils,
  type Object3D,
} from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

export interface CameraRig {
  perspective: PerspectiveCamera;
  orthographic: OrthographicCamera;
  controls: OrbitControls;
  active: () => PerspectiveCamera | OrthographicCamera;
  setMode(mode: CameraMode): void;
  resize(width: number, height: number): void;
  frameObject(object: Object3D): void;
  dispose(): void;
}

export type CameraMode = 'perspective' | 'topDown';

/**
 * Orbit behavior constraints per design doc:
 * - rotation around vertical axis always allowed
 * - polar angle clamped to avoid disorienting underside views
 * - zoom bounded to keep the model legible
 * - damping enabled for polish
 */
export function createCameraRig(domElement: HTMLElement, width: number, height: number): CameraRig {
  const perspective = new PerspectiveCamera(42, width / height, 0.1, 200);
  perspective.position.set(12, 9, 14);

  const orthoSize = 16;
  const aspect = width / height;
  const orthographic = new OrthographicCamera(
    -orthoSize * aspect,
    orthoSize * aspect,
    orthoSize,
    -orthoSize,
    0.1,
    200,
  );
  orthographic.position.set(0, 40, 0.01); // tiny Z offset so "up" vector resolves
  orthographic.lookAt(0, 0, 0);

  const controls = new OrbitControls(perspective, domElement);
  controls.enableDamping = true;
  controls.dampingFactor = 0.075;
  controls.target.set(0, 2, 0);
  controls.minPolarAngle = MathUtils.degToRad(15);
  controls.maxPolarAngle = MathUtils.degToRad(82);
  controls.minDistance = 5;
  controls.maxDistance = 45;
  controls.zoomSpeed = 0.9;
  controls.rotateSpeed = 0.85;

  let mode: CameraMode = 'perspective';

  const rig: CameraRig = {
    perspective,
    orthographic,
    controls,
    active() {
      return mode === 'perspective' ? perspective : orthographic;
    },
    setMode(next) {
      if (mode === next) return;
      mode = next;
      if (next === 'topDown') {
        controls.object = orthographic;
        controls.minPolarAngle = 0;
        controls.maxPolarAngle = MathUtils.degToRad(5);
      } else {
        controls.object = perspective;
        controls.minPolarAngle = MathUtils.degToRad(15);
        controls.maxPolarAngle = MathUtils.degToRad(82);
      }
      controls.update();
    },
    resize(w, h) {
      const ar = w / h;
      perspective.aspect = ar;
      perspective.updateProjectionMatrix();
      orthographic.left = -orthoSize * ar;
      orthographic.right = orthoSize * ar;
      orthographic.top = orthoSize;
      orthographic.bottom = -orthoSize;
      orthographic.updateProjectionMatrix();
    },
    frameObject(object) {
      const box = new Vector3();
      object.getWorldPosition(box);
      controls.target.set(0, 2, 0);
      controls.update();
    },
    dispose() {
      controls.dispose();
    },
  };

  return rig;
}
